import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  ReactiveFormsModule,
  NonNullableFormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputComponent } from '../../ui/input/input.component';
import { ButtonComponent } from '../../ui/button/button.component';
import { PasswordStrengthComponent } from '../../ui/password-strength/password-strength.component';
import { CheckboxComponent } from '../../ui/checkbox/checkbox.component';
import { RecaptchaService, RecaptchaNotLoadedError } from '../../ui/recaptcha/recaptcha.component';
import { VerificationModalComponent, VerificationModalData } from '../../ui/verification-modal/verification-modal.component';
import { SuccessModalComponent, SuccessModalData } from '../../ui/success-modal/success-modal.component';
import { LanguageSelectorComponent } from '../../ui/language-selector/language-selector.component';
import { GoogleButtonComponent } from '../../ui/google-button/google-button.component';
import { AppleButtonComponent } from '../../ui/apple-button/apple-button.component';
import { AuthService, LanguageService, StorageService, UserService } from '@trading-fe/services';
import { FeError } from '@trading-fe/interceptors';
import { ThemeService } from '../../services/theme.service';
import { SocialAuthService } from '../../services/social-auth.service';
import { ThemeToggleComponent } from '../../ui/theme-toggle/theme-toggle.component';
import { ResendCooldownService } from '../../services/resend-cooldown.service';
import { ModalService } from '../../ui/modal/modal.service';
import { ModalRef } from '../../ui/modal/modal-ref';

@Component({
  selector: 'app-create-account',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslateModule,
    InputComponent,
    ButtonComponent,
    PasswordStrengthComponent,
    CheckboxComponent,
    LanguageSelectorComponent,
    GoogleButtonComponent,
    AppleButtonComponent,
    ThemeToggleComponent,
  ],
  templateUrl: './create-account.component.html',
  styleUrl: './create-account.component.scss',
})
export class CreateAccountComponent implements OnInit {
  readonly isDark = inject(ThemeService).isDark;
  private fb = inject(NonNullableFormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);
  readonly langService = inject(LanguageService);
  private recaptchaService = inject(RecaptchaService);
  private userService = inject(UserService);
  private cooldown = inject(ResendCooldownService);
  private modalService = inject(ModalService);
  private socialAuth = inject(SocialAuthService);
  private storage = inject(StorageService);

  private verificationRef: ModalRef | null = null;
  private verificationError = signal('');
  private verificationLoading = signal(false);

  showPasswordStrength = signal(false);
  showPassword = signal(false);
  showConfirmPassword = signal(false);
  loading = signal(false);

  form = this.fb.group(
    {
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, this.passwordValidator]],
      confirmPassword: ['', [Validators.required]],
      termsAccepted: [false, [Validators.requiredTrue]],
    },
    { validators: this.passwordMatchValidator }
  );

  readonly termsUrl =
    'https://cubeinvest.am/storage/reports/\u0554\u0561\u0572\u0561\u0584\u0561\u056f\u0561\u0576\u0578\u0582\u0569\u0575\u0578\u0582\u0576_\u0533\u0561\u0572\u057f\u0576\u056b\u0578\u0582\u0569\u0575\u0561\u0576_Cube_Invest.pdf.pdf';

  ngOnInit(): void {
    const pendingEmail = sessionStorage.getItem('email');
    if (pendingEmail) {
      this.form.controls.email.setValue(pendingEmail);
      this.openVerificationWithResend();
    }
  }

  get passwordValue(): string {
    return this.form.get('password')?.value || '';
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  get emailError(): string {
    const ctrl = this.form.controls.email;
    if (!ctrl.touched) return '';
    if (ctrl.hasError('required')) return this.translate.instant('ERRORS.REQUIRED_EMAIL');
    if (ctrl.hasError('emailInUse')) return this.translate.instant('ERRORS.EMAIL_IN_USE');
    if (ctrl.hasError('invalidEmail')) return this.translate.instant('ERRORS.INVALID_EMAIL');
    if (ctrl.hasError('email')) return this.translate.instant('ERRORS.INVALID_EMAIL');
    return '';
  }

  get passwordError(): string {
    const ctrl = this.form.controls.password;
    if (!ctrl.touched) return '';
    if (ctrl.hasError('required')) return this.translate.instant('ERRORS.REQUIRED_PASSWORD');
    if (ctrl.hasError('invalidPassword')) return this.translate.instant('BACKEND_ERRORS.INVALID_PASSWORD');
    return '';
  }

  get confirmPasswordError(): string {
    if (this.form.hasError('passwordMismatch') && this.form.controls.confirmPassword.touched) {
      return this.translate.instant('ERRORS.PASSWORDS_DO_NOT_MATCH');
    }
    return '';
  }

  togglePassword(): void {
    this.showPassword.update(v => !v);
  }

  toggleConfirmPassword(): void {
    this.showConfirmPassword.update(v => !v);
  }

  onPasswordFocus(): void {
    this.showPasswordStrength.set(true);
  }

  onPasswordBlur(): void {
    if (!this.passwordValue && !this.form.get('confirmPassword')?.value) {
      this.showPasswordStrength.set(false);
    }
  }

  async onGoogleLogin(): Promise<void> {
    this.loading.set(true);
    try {
      const idToken = await this.socialAuth.signInWithGoogle();
      this.authService
        .googleAuth(idToken)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result) => this.handleSocialLoginSuccess(result),
          error: (err) => this.handleSocialLoginError(err),
        });
    } catch {
      this.loading.set(false);
    }
  }

  async onAppleLogin(): Promise<void> {
    this.loading.set(true);
    try {
      const idToken = await this.socialAuth.signInWithApple();
      this.authService
        .appleAuth(idToken)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result) => this.handleSocialLoginSuccess(result),
          error: (err) => this.handleSocialLoginError(err),
        });
    } catch {
      this.loading.set(false);
    }
  }

  private handleSocialLoginSuccess(result: any): void {
    this.authService.setStorage(result, true);

    if (this.storage.getItem('tfa') === 'needToCheck') {
      this.loading.set(false);
      this.navigateAfterAuth();
      return;
    }

    this.authService.fetchGtnToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.navigateAfterAuth();
        },
        error: () => {
          this.loading.set(false);
          this.navigateAfterAuth();
        },
      });
  }

  private handleSocialLoginError(err: unknown): void {
    this.loading.set(false);
    if (err instanceof FeError) {
      this.message.error(
        this.translate.instant(`BACKEND_ERRORS.${err.type}`) !== `BACKEND_ERRORS.${err.type}`
          ? this.translate.instant(`BACKEND_ERRORS.${err.type}`)
          : err.message || this.translate.instant('ERRORS.REGISTRATION_FAILED')
      );
    } else {
      this.message.error(this.translate.instant('ERRORS.REGISTRATION_FAILED'));
    }
  }

  async onSubmit(): Promise<void> {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    this.loading.set(true);

    try {
      const captcha = await this.recaptchaService.execute('REGISTER');

      const data = {
        email: this.form.value.email?.trim(),
        password: this.form.value.password,
        captcha,
        lang: this.langService.currentLang,
      };

      this.authService
        .register(data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.loading.set(false);
            sessionStorage.setItem('email', data.email!);
            this.cooldown.markSent(data.email!);
            this.openVerificationModal(60);
          },
          error: (err) => {
            this.loading.set(false);
            this.handleRegistrationError(err);
          },
        });
    } catch (err) {
      this.loading.set(false);
      const key = err instanceof RecaptchaNotLoadedError
        ? 'BACKEND_ERRORS.RECAPTCHA_NOT_LOADED'
        : 'BACKEND_ERRORS.INVALID_RECAPTCHA';
      this.message.error(this.translate.instant(key));
    }
  }

  private openVerificationModal(countdown: number): void {
    this.verificationError.set('');
    this.verificationLoading.set(false);

    const data: VerificationModalData = {
      email: this.form.value.email || '',
      errorText: this.verificationError,
      loading: this.verificationLoading,
      initialCountdown: countdown,
      onVerify: (code: string) => this.onVerify(code),
      onResend: () => this.onResend(),
    };

    this.verificationRef = this.modalService.open(VerificationModalComponent, { data });
    this.verificationRef.afterClosed$.subscribe(() => {
      this.verificationError.set('');
      sessionStorage.removeItem('email');
      this.verificationRef = null;
    });
  }

  private openSuccessModal(): void {
    const ref = this.modalService.open<SuccessModalComponent, SuccessModalData>(
      SuccessModalComponent,
    );
    ref.afterClosed$.subscribe(() => {
      this.navigateAfterAuth();
    });
  }

  private openVerificationWithResend(): void {
    const email = this.form.value.email!;
    const remaining = this.cooldown.getRemaining(email);
    if (remaining > 0) {
      this.openVerificationModal(remaining);
    } else {
      this.openVerificationModal(60);
      this.onResend(true);
    }
  }

  private onVerify(code: string): void {
    this.verificationLoading.set(true);
    this.verificationError.set('');

    this.authService
      .verifyEmail(this.form.value.email!, code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.verificationLoading.set(false);
          sessionStorage.removeItem('email');
          if (res) {
            this.authService.setStorage(res, true);
          }
          this.verificationRef?.close();
          this.openSuccessModal();
        },
        error: (err) => {
          this.verificationLoading.set(false);
          if (err instanceof FeError) {
            const key = `BACKEND_ERRORS.${err.type}`;
            const translated = this.translate.instant(key);
            this.verificationError.set(translated !== key ? key : err.message);
          } else {
            this.verificationError.set('ERRORS.INVALID_CODE');
          }
        },
      });
  }

  private onResend(silent = false): void {
    const email = this.form.value.email!;
    this.verificationError.set('');
    this.cooldown.markSent(email);
    this.authService
      .resendVerification(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (!silent) {
            this.message.success(this.translate.instant('ERRORS.RESEND_SUCCESS'));
          }
        },
        error: (err) => {
          if (err instanceof FeError) {
            const seconds = this.cooldown.updateFromError(email, err.message);
            if (seconds > 0) {
              const modal = this.verificationRef?.componentInstance as VerificationModalComponent;
              modal?.startCountdown(seconds);
              if (silent) return;
            }
            const key = `BACKEND_ERRORS.${err.type}`;
            const translated = this.translate.instant(key);
            this.verificationError.set(translated !== key ? key : err.message);
          } else {
            if (!silent) {
              this.verificationError.set('ERRORS.RESEND_FAILED');
            }
          }
        },
      });
  }

  private navigateAfterAuth(): void {
    const lang = this.langService.currentLang || 'en';
    const userId = this.authService.userId;
    if (!userId) {
      this.router.navigate([lang, 'questionnaire'], { replaceUrl: true });
      return;
    }

    this.userService.get(userId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (user) => {
        const showQuestionary = user?.profile?.termsAccepted != true;
        if (showQuestionary) {
          this.router.navigate([lang, 'questionnaire'], { replaceUrl: true });
        } else {
          this.router.navigate([lang], { replaceUrl: true });
        }
      },
      error: () => {
        this.router.navigate([lang], { replaceUrl: true });
      },
    });
  }

  private handleRegistrationError(err: unknown): void {
    if (err instanceof FeError) {
      switch (err.type) {
        case 'EMAIL_IN_USE':
          this.form.controls.email.setErrors({ emailInUse: true });
          return;
        case 'INVALID_EMAIL':
          this.form.controls.email.setErrors({ invalidEmail: true });
          return;
        case 'INVALID_PASSWORD':
          this.form.controls.password.setErrors({ invalidPassword: true });
          return;
        case 'NEED_VERIFICATION_VIA_EMAIL':
          sessionStorage.setItem('email', this.form.value.email!);
          this.openVerificationWithResend();
          return;
        case 'INVALID_RECAPTCHA':
          this.message.error(this.translate.instant('BACKEND_ERRORS.INVALID_RECAPTCHA'));
          return;
        case 'ERROR_VALIDATION':
          this.message.error(this.translate.instant('BACKEND_ERRORS.ERROR_VALIDATION'));
          return;
        case 'ACCOUNT_BLOCKED':
          this.message.error(this.translate.instant('BACKEND_ERRORS.ACCOUNT_BLOCKED'));
          return;
        case 'ACCOUNT_INACTIVE':
          this.message.error(this.translate.instant('BACKEND_ERRORS.ACCOUNT_INACTIVE'));
          return;
        case 'AGE_RESTRICTION':
          this.message.error(this.translate.instant('BACKEND_ERRORS.AGE_RESTRICTION'));
          return;
        case 'ERROR_TOO_MANY_REQUESTS':
          this.message.error(this.translate.instant('BACKEND_ERRORS.ERROR_TOO_MANY_REQUESTS'));
          return;
        default:
          this.message.error(err.message || this.translate.instant('ERRORS.REGISTRATION_FAILED'));
          return;
      }
    }

    this.message.error(
      err instanceof Error ? err.message : this.translate.instant('ERRORS.REGISTRATION_FAILED')
    );
  }

  private passwordValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const errors: ValidationErrors = {};
    if (value.length < 6) errors['minLength'] = true;
    if (!/[A-Z]/.test(value)) errors['uppercase'] = true;
    if (!/[a-z]/.test(value)) errors['lowercase'] = true;
    if (!/[0-9]/.test(value)) errors['number'] = true;

    return Object.keys(errors).length ? errors : null;
  }

  private passwordMatchValidator(
    group: AbstractControl
  ): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }
}
